import { useEffect, useState, useMemo } from 'react'
import { useAuthState, useClient } from '@based/sdk/react'
import { useTranslations } from '@i18n/useLanguage'
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons'
import { fullName } from '@app/util/helpers'
import { INITIATIVE_USER_ROLES, InitiativeUserRole } from '@schema/initiative'

import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { IconButton } from '@app/components/core/button/IconButton'
import { CollapsibleSection } from '@app/components/blocks/collapsibleSection/CollapsibleSection'
import Column from '@app/components/layout/Column'
import { Recipient } from '@app/util/contexts/MessageBoxContext'

import listInitiativeFunction from 'functions/app/dashboard/profile/initiative/list/based.config'
import listRecipientsFunction from 'functions/app/dashboard/communications/list-initiative-recipients/based.config'
import { useMessageBox } from '@app/util/hooks/useMessageBox'
import { UserList } from '@app/components/blocks/userList/UserList'

type RawRecipient = {
  id: number
  avatar?: { src: string }
  firstName: string
  middlePart?: string
  lastName: string
  handle?: string
  $role: InitiativeUserRole
}

export type CorrespondentModel = {
  id: number
  avatar?: string
  name: string
  roleLabel: string
  handle?: string
  rawRole: InitiativeUserRole
}

export type InitiativeWithRecipients = {
  id: number
  title: string
  isAdministered: boolean
  recipients: CorrespondentModel[]
}

type InitiativeDict = Record<number, InitiativeWithRecipients>

export const PeopleToMessagePanel = () => {
  const t = useTranslations()
  const client = useClient()
  const { userId } = useAuthState()

  const [adminIds, setAdminIds] = useState<number[]>([])
  const [memberIds, setMemberIds] = useState<number[]>([])
  const [initiativesMap, setInitiativesMap] = useState<InitiativeDict>({})

  useEffect(() => {
    if (!userId) {
      return
    }

    const unsubAdmin = client
      .query(listInitiativeFunction.name, {
        userId,
        role: [INITIATIVE_USER_ROLES.owner, INITIATIVE_USER_ROLES.admin],
        includeAllInitiativeUsers: true,
      })
      .subscribe((res: { id: number }[]) => {
        setAdminIds(res.map((i) => i.id))
      })

    const unsubMember = client
      .query(listInitiativeFunction.name, {
        userId,
        role: [
          INITIATIVE_USER_ROLES.member,
          INITIATIVE_USER_ROLES.follower,
          INITIATIVE_USER_ROLES.applicant,
        ],
        includeAllInitiativeUsers: true,
      })
      .subscribe((res: { id: number }[]) => {
        setMemberIds(res.map((i) => i.id))
      })

    return () => {
      unsubAdmin()
      unsubMember()
    }
  }, [client, userId])

  useEffect(() => {
    const targets = [
      ...adminIds.map((id) => ({ id, isAdm: true })),
      ...memberIds.map((id) => ({ id, isAdm: false })),
    ]

    const unsubs = targets.map(({ id, isAdm }) => {
      return client
        .query(listRecipientsFunction.name, { userId, initiativeId: id })
        .subscribe((result) => {
          const recipients: CorrespondentModel[] = result.users.map(
            (u: RawRecipient) => ({
              id: u.id,
              avatar: u.avatar?.src,
              name: fullName(u),
              rawRole: u.$role,
              handle: u.handle,
              roleLabel:
                u.$role === INITIATIVE_USER_ROLES.owner
                  ? t.app.common.initiativeUserRole[INITIATIVE_USER_ROLES.admin]
                  : t.app.common.initiativeUserRole[u.$role],
            }),
          )

          setInitiativesMap((prev) => ({
            ...prev,
            [result.id]: {
              id: result.id,
              title: result.title,
              isAdministered: isAdm,
              recipients,
            },
          }))
        })
    })

    return () => unsubs.forEach((u) => u())
  }, [adminIds, memberIds, client, userId, t.app.common.initiativeUserRole])

  const initiatives = useMemo(
    () =>
      Object.values(initiativesMap).sort((x, y) =>
        x.isAdministered ? (y.isAdministered ? 0 : -1) : 1,
      ),
    [initiativesMap],
  )

  return (
    <Column
      alignItems="stretch"
      gap="2rem"
      style={{
        backgroundColor: 'var(--palette-white)',
        padding: '1.5rem 1rem',
        borderRadius: '1.25rem',
        flex: '1 1 20rem',
        minWidth: '20rem',
      }}
    >
      <div>
        <Title.H4>{t.app.dashboard.messages.peopleToMessageTitle}</Title.H4>
        <Text.Small color="var(--palette-blue-500)">
          {t.app.dashboard.messages.peopleToMessageDescription}
        </Text.Small>
      </div>

      <Column alignItems="stretch" gap="2rem">
        {initiatives.map((initiative, i) => (
          <CorrespondentsPanel
            key={initiative.id}
            initiative={initiative}
            isOpenByDefault={i === 0}
          />
        ))}
      </Column>
    </Column>
  )
}

const CorrespondentsPanel = ({
  initiative,
  isOpenByDefault = false,
}: {
  initiative: InitiativeWithRecipients
  isOpenByDefault?: boolean
}) => {
  const { title, recipients } = initiative

  const grouped = useMemo(() => {
    const map = recipients.reduce(
      (acc, user) => {
        const key = user.roleLabel
        ;(acc[key] = acc[key] || []).push(user)
        return acc
      },
      {} as Record<string, CorrespondentModel[]>,
    )

    return Object.entries(map).map(([role, users]) => ({ role, users }))
  }, [recipients])

  if (!recipients || recipients.length === 0) {
    return null
  }

  return (
    <CollapsibleSection
      title={<Title.H5 style={{ marginBottom: 0 }}>{title}</Title.H5>}
      isOpenByDefault={isOpenByDefault}
    >
      <Column alignItems="stretch">
        {grouped.map((group) => (
          <CorrespondentsGroup
            key={group.role}
            isOpenByDefault={false}
            groupName={group.role}
            users={group.users}
            initiativeId={initiative.id}
            allRecipients={initiative.recipients.map((u) => ({
              id: u.id,
              name: u.name,
              avatar: u.avatar,
              role: u.rawRole,
            }))}
          />
        ))}
      </Column>
    </CollapsibleSection>
  )
}

const CorrespondentsGroup = ({
  groupName,
  isOpenByDefault,
  users,
  allRecipients,
  initiativeId,
}: {
  groupName?: string
  isOpenByDefault?: boolean
  users: CorrespondentModel[]
  allRecipients: Recipient[]
  initiativeId: number
}) => {
  const { showMessageBox } = useMessageBox()

  const userList = useMemo(
    () => (
      <UserList
        users={users.map((u) => ({
          id: u.id,
          name: u.name,
          avatar: u.avatar,
          handle: u.handle,
          description: u.roleLabel,
          action: (
            <IconButton
              size="small"
              variant="secondary"
              icon={faEnvelope}
              onClick={() => showMessageBox(allRecipients, initiativeId, u.id)}
            />
          ),
        }))}
      />
    ),
    [users, allRecipients, initiativeId, showMessageBox],
  )

  if (!groupName) {
    return userList
  }

  const groupSize = users.length > 1 ? ` (${users.length})` : ''

  return (
    <CollapsibleSection
      title={
        <Text.Subtitle style={{ fontWeight: 500 }}>
          {groupName + groupSize}
        </Text.Subtitle>
      }
      lightIcon
      isOpenByDefault={isOpenByDefault}
    >
      {userList}
    </CollapsibleSection>
  )
}
